import { useCallback, useMemo } from 'react';

import { useTranslation } from '@lichtblick/i18next';
import { Check, PeopleAltTwoTone, Send } from '@lichtblick/icons';
import { TextVariants } from '@lichtblick/theme';
import { Author, MessageContent, ApprovalState } from '@lichtblick/types';

import {
  AuthorInfo,
  Column,
  Container,
  DateAndTeam,
  ImageContainer,
  Message,
  MultipleChoiceItem,
  Row,
  ImageRow,
  ImageWithCheckbox,
  CheckboxContainer,
  SelectAllButtonContainer,
} from './ChatMessageCard.styles';
import { formatTimestampDate } from './utils';

import { Button } from '../Button';
import { ChatAvatar } from '../ChatAvatar';
import { ChatMessageImageRow } from '../ChatMessageImageRow';
import { Checkbox } from '../Checkbox';
import { Chip } from '../Chip';
import { Image } from '../Image';
import { Text } from '../Text';

export type ChatMessageCardProps = {
  author: Author;
  comment?: string | null;
  content?: MessageContent;
  currentUserId?: number;
  deselectedImages: string[];
  isLatestMessage?: boolean;
  itemDecisions?: Array<{
    approvalState: ApprovalState;
    arrayIndex: number;
    decisionMadeAtUtc?: string | null;
    decisionMadeById?: number | null;
    mediaId?: number | null;
    rejectReason?: string | null;
  }>;
  setDeselectedImages: (images: string[]) => void;
  status?: ApprovalState;
  timestamp: string;
};

export const ChatMessageCard = ({
  author,
  comment,
  content,
  currentUserId,
  deselectedImages,
  isLatestMessage = false,
  itemDecisions,
  setDeselectedImages,
  status,
  timestamp,
}: ChatMessageCardProps) => {
  const { t } = useTranslation('common');

  const handleImageToggle = useCallback(
    (imageUrl: string) => {
      if (deselectedImages.includes(imageUrl)) {
        setDeselectedImages(deselectedImages.filter((url) => url !== imageUrl));
      } else {
        setDeselectedImages([...deselectedImages, imageUrl]);
      }
    },
    [deselectedImages, setDeselectedImages],
  );

  const allImageUrls = useMemo(() => {
    if (!content?.image) {
      return [];
    }

    return content.image.map((img) => img.url);
  }, [content?.image]);

  const areAllImagesSelected = useMemo(() => {
    if (allImageUrls.length === 0) {
      return true;
    }

    return allImageUrls.every((url) => !deselectedImages.includes(url));
  }, [allImageUrls, deselectedImages]);

  const handleToggleAllImages = useCallback(() => {
    if (areAllImagesSelected) {
      setDeselectedImages([...deselectedImages, ...allImageUrls]);
    } else {
      setDeselectedImages(deselectedImages.filter((url) => !allImageUrls.includes(url)));
    }
  }, [areAllImagesSelected, deselectedImages, allImageUrls, setDeselectedImages]);

  const getImageApprovalState = useCallback(
    (index: number): ApprovalState | undefined => {
      if (!itemDecisions) {
        return undefined;
      }

      const decision = itemDecisions.find((d) => d.arrayIndex === index);

      return decision?.approvalState;
    },
    [itemDecisions],
  );

  const authorName = () => {
    if (author.isCraftsman) {
      return author.name;
    }

    if (author.id === currentUserId) {
      return t('details.you');
    }

    return author.name;
  };

  return (
    <Container>
      <AuthorInfo $isCraftsman={author.isCraftsman}>
        <ChatAvatar isCraftsman={author.isCraftsman} />
        <Column $isCraftsman={author.isCraftsman}>
          <Text $variant={TextVariants.title.l}>{authorName()}</Text>
          <DateAndTeam>
            {author.isCraftsman && author.team && (
              <Row>
                <PeopleAltTwoTone />
                <Text $variant={TextVariants.body.s}>
                  {t('common:details.categories.team')}
                  <Text $variant={TextVariants.body.sMedium}>{author.team}</Text>
                </Text>
              </Row>
            )}
            <Row>
              <Send />
              <Text $variant={TextVariants.body.s}>
                {t('common:details.categories.sent')}
                <Text $variant={TextVariants.body.sMedium}>{formatTimestampDate(timestamp)}</Text>
              </Text>
            </Row>
          </DateAndTeam>
        </Column>
      </AuthorInfo>
      <Message $isCraftsman={author.isCraftsman}>
        {status && <Chip size="large" variant={status} />}
        {comment && <Text $variant={TextVariants.body.l}>{comment}</Text>}
        {content?.text && <Text $variant={TextVariants.header.s}>{content.text}</Text>}
        {content?.multipleChoice &&
          content?.multipleChoice.map((text) => (
            <MultipleChoiceItem $isYesAnswer key={`question-${text}`}>
              <Check data-testid="chat-message-check" />
              <Text $variant={TextVariants.body.l}>{text}</Text>
            </MultipleChoiceItem>
          ))}
        {content?.image && (
          <>
            {isLatestMessage &&
              content.image.length > 1 &&
              !content.image.some((_, idx) => {
                const imageApprovalState = getImageApprovalState(idx);

                return (
                  imageApprovalState === ApprovalState.Rejected ||
                  imageApprovalState === ApprovalState.ServerApproved ||
                  imageApprovalState === ApprovalState.ClientApproved
                );
              }) && (
                <SelectAllButtonContainer>
                  <Button
                    label={areAllImagesSelected ? t('ui.deselectAll') : t('ui.selectAll')}
                    onClick={handleToggleAllImages}
                    size="small"
                    variant="secondary"
                  />
                </SelectAllButtonContainer>
              )}
            <ImageContainer>
              {content.image.map((image, idx) => {
                const imageApprovalState = getImageApprovalState(idx);
                const isRejected = imageApprovalState === ApprovalState.Rejected;
                const isApproved =
                  imageApprovalState === ApprovalState.ServerApproved ||
                  imageApprovalState === ApprovalState.ClientApproved;
                const _isPending =
                  imageApprovalState === ApprovalState.Pending || imageApprovalState === ApprovalState.Resubmitted;
                const hasDecision = isRejected || isApproved;
                const showCheckbox = isLatestMessage && !hasDecision;

                return (
                  <ImageWithCheckbox
                    $isLatestMessage={isLatestMessage}
                    $isSelected={hasDecision ? !isRejected : !deselectedImages.includes(image.url)}
                    key={`image-${image.url}`}
                  >
                    {showCheckbox && (
                      <CheckboxContainer>
                        <Checkbox
                          isChecked={!deselectedImages.includes(image.url)}
                          onChange={() => handleImageToggle(image.url)}
                        />
                      </CheckboxContainer>
                    )}
                    {hasDecision && (
                      <CheckboxContainer>
                        <Chip size="small" text={isApproved ? t('approved') : undefined} variant={imageApprovalState} />
                      </CheckboxContainer>
                    )}
                    <ChatMessageImageRow idx={idx} image={image} />
                  </ImageWithCheckbox>
                );
              })}
            </ImageContainer>
          </>
        )}
        {content?.signature && (
          <ImageContainer>
            <ImageRow>
              <Image alt="test" counter={1} expectedMaxWidth={360} fill hasModal src={content.signature.image.url} />
            </ImageRow>
          </ImageContainer>
        )}
        {content?.isYesNo && (
          <Chip
            shouldShowIcon={false}
            size="large"
            text={content.isYesNo ? t('yes') : t('no')}
            variant="descriptive-lightblue"
          />
        )}
      </Message>
    </Container>
  );
};
